import { type SxProps, type Theme } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { mergeSxProps } from "@src/appV2/lib";

import { useNavigationMenu } from "../../AppLayout/Mobile/NavigationMenuContext";
import { Button } from "../Button";
import { HamburgerIcon } from "../FloatingNavigation/HamburgerIcon";

export interface HeaderMenuButtonProps {
  sx?: SxProps<Theme>;
}

/**
 * Inline hamburger button rendered to the left of a page's headline on mobile.
 * Self-gating: renders only when the mobile navigation provider is mounted,
 * i.e. inside the mobile app shell. Gating on the provider rather than the
 * viewport keeps the button in lockstep with the shell, which is not
 * viewport-bound in the native app. Opens the drawer via the navigation
 * context.
 */
export function HeaderMenuButton(props: HeaderMenuButtonProps) {
  const { sx } = props;
  const { tu } = useTu("navigation");
  const navigationMenu = useNavigationMenu();

  if (!navigationMenu) {
    return null;
  }

  return (
    <Button
      variant="muted"
      size="medium"
      aria-label={tu("Open menu")}
      sx={mergeSxProps((theme) => {
        const buttonHeight = theme.size?.button.medium.height;
        return {
          paddingX: theme.size?.button.medium.paddingY,
          paddingY: theme.size?.button.medium.paddingY,
          borderRadius: "50%",
          width: buttonHeight,
          minWidth: buttonHeight,
          aspectRatio: "1",
        };
      }, sx)}
      onClick={() => {
        navigationMenu.open();
      }}
    >
      <HamburgerIcon />
    </Button>
  );
}
